import { Box, Skeleton } from '@mui/material';

export const TopRowSkeleton = ({ count = 6 }) => {
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}>
      {Array.from(new Array(count)).map((i, idx) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            flex: '1',
            borderRight: idx === count - 1 ? '' : '1px solid #cbd5e0',
            justifyContent: 'center',
          }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="rectangular" height={'15px'} width={'80%'} sx={{ borderRadius: '8px' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="rectangular" height={'25px'} width={'80%'} sx={{ borderRadius: '8px' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Body of the overview's top card while it loads: one placeholder per tile (icon, count, label) on the
// same strip, so nothing shifts when the counts land.
export const TopRowTilesSkeleton = ({ count = 6 }) => (
  <Box className="KubernetesOverview__TopTiles">
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} className="KubernetesOverview__TopTile">
        <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
        <Box className="KubernetesOverview__TopTileText" sx={{ flex: 1 }}>
          <Skeleton variant="text" width={48} height={20} />
          <Skeleton variant="text" width="80%" height={14} />
        </Box>
      </Box>
    ))}
  </Box>
);

export const RecentFindingsSkeleton = ({ isRecentFindings }) => {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '15px',
        // border: '1px solid red',
      }}>
      {Array.from(new Array(isRecentFindings ? 5 : 4)).map(i => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}>
            <Skeleton variant="rectangular" height={'15px'} width={'80%'} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" height={'15px'} width={'80%'} sx={{ borderRadius: '8px' }} />
          </Box>
          <Skeleton variant="rectangular" height={'40px'} width={'60px'} sx={{ borderRadius: '8px' }} />
        </Box>
      ))}
    </Box>
  );
};

export const IdentityByRisk = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        // border: '1px solid red',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <Skeleton variant="circular" height={'190px'} width={'190px'} />
      <Skeleton variant="rectangular" width={'300px'} height={'120px'} />
    </Box>
  );
};

//  Skeleton wrapper for IdentityOverview with ContentBox structure
export const IdentityOverviewSkeleton = () => {
  return (
    <Box
      sx={{
        marginBottom: '15px',
        border: '1px solid #cbd5e0',
        borderRadius: '5px',
        backgroundColor: '#fff',
      }}>
      {/* Title bar */}
      <Box
        sx={{
          height: '40px',
          borderBottom: '1px solid #cbd5e0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
        }}>
        <Skeleton variant="rectangular" height={'20px'} width={'140px'} />
      </Box>
      {/* Content */}
      <Box sx={{ padding: '16px' }}>
        <TopRowSkeleton count={5} />
      </Box>
    </Box>
  );
};

//  Skeleton wrapper for RiskOverTime with ContentBox structure
export const RiskOverTimeSkeleton = () => {
  return (
    <Box
      sx={{
        border: '1px solid #cbd5e0',
        borderRadius: '5px',
        backgroundColor: '#fff',
        height: '288px', // 40px title + 248px content
      }}>
      {/* Title bar */}
      <Box
        sx={{
          height: '40px',
          borderBottom: '1px solid #cbd5e0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
        }}>
        <Skeleton variant="rectangular" height={'20px'} width={'160px'} />
      </Box>
      {/* Content */}
      <Box sx={{ padding: '16px', height: '248px', display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rectangular" height={'208px'} width={'100%'} />
      </Box>
    </Box>
  );
};

//  Skeleton wrapper for MostActiveActor with ContentBox structure
export const MostActiveActorSkeleton = () => {
  return (
    <Box
      sx={{
        border: '1px solid #cbd5e0',
        borderRadius: '5px',
        backgroundColor: '#fff',
        height: '288px', // 40px title + 248px content
      }}>
      {/* Title bar */}
      <Box
        sx={{
          height: '40px',
          borderBottom: '1px solid #cbd5e0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
        }}>
        <Skeleton variant="rectangular" height={'20px'} width={'150px'} />
      </Box>
      {/* Content - mimic the two-column layout */}
      <Box sx={{ height: '248px', padding: '16px' }}>
        <Box display="flex" justifyContent="space-between" marginBottom="10px">
          <Skeleton width="200px" height="25px" />
          <Skeleton width="300px" height="25px" />
        </Box>
        <Box display="flex" justifyContent="space-between" height="calc(100% - 35px)">
          <Box display="flex" flexDirection="column" gap="8px" width="45%">
            {Array.from(new Array(5)).map((_, index) => (
              <Skeleton key={index} width="100%" height="20px" />
            ))}
          </Box>
          <Box display="flex" flexDirection="column" gap="8px" width="50%">
            {Array.from(new Array(5)).map((_, index) => (
              <Skeleton key={index} width="100%" height="20px" />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

//  Skeleton wrapper for IdentitiesByRisk/Type with ContentBox structure
export const PieChartSkeleton = () => {
  return (
    <Box
      sx={{
        border: '1px solid #cbd5e0',
        borderRadius: '5px',
        backgroundColor: '#fff',
        height: '255px', // 40px title + 215px content
      }}>
      {/* Title bar */}
      <Box
        sx={{
          height: '40px',
          borderBottom: '1px solid #cbd5e0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
        }}>
        <Skeleton variant="rectangular" height={'20px'} width={'150px'} />
      </Box>
      {/* Content */}
      <Box
        sx={{
          padding: '16px',
          height: '215px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Skeleton variant="circular" height={'180px'} width={'180px'} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '45%' }}>
          {Array.from(new Array(4)).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={'25px'} width={'100%'} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
